import React, { useMemo, useState } from 'react';
import { BuildingIcon, FileUpIcon, HistoryIcon, MailIcon, ShieldAlertIcon, ShieldCheckIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { AcademicBadge } from '../../components/verification/AcademicBadge';
import { useVerification } from '../../contexts/VerificationContext';
import { LEVEL_LABEL, LEVEL_MEANING, LEVEL_ORDER, METHOD_LABEL, STATE_LABEL, STATUS_LABEL, daysUntil, formatDate, levelRank, maskIdentifier } from '../../utils/verification';
import type { AcademicRecord, VerificationLevel } from '../../types/verification';
const TABS = ['Your identity', 'Verify', 'What it unlocks', 'History'] as const;

/**
 * The student's academic identity. Its whole job is to keep "what you told us"
 * visibly apart from "what someone confirmed", at every level of the screen.
 */
export function AcademicIdentity() {
  const {
    records,
    institutions,
    primary,
    trust,
    policies,
    can,
    cases
  } = useVerification();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Your identity');
  const institutionOf = (id: string) => institutions.find(entry => entry.id === id);
  const myCases = useMemo(() => cases.filter(entry => entry.handle === 'saviour'), [cases]);
  return <div className="space-y-6" data-id="element-9837" data-mp="256l96q1xj6ei">
      <PageHeader eyebrow="Identity" title="Academic identity" description="Campuzon keeps three things apart: what you have told us, what someone has actually confirmed, and what that lets you do. This page shows all three." actions={primary && <AcademicBadge record={primary} institutionName={institutionOf(primary.institutionId)?.shortName ?? 'Institution'} owner data-id="element-9839" data-mp="3blvmxw1uipfp" />} data-id="element-9838" data-mp="257wb0kbte42z" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Academic identity sections" data-id="element-9840" data-mp="3nmvcph61o5px" />

      {tab === 'Your identity' && <div className="space-y-5" data-id="element-9841" data-mp="1hglef82hlbx">
          {records.length === 0 ? <Panel data-id="element-9842" data-mp="1fdt1ojfzg41">
              <EmptyState icon={<BuildingIcon className="h-5 w-5" data-id="element-9844" data-mp="3005aaepsa94s" />} title="No academic record yet" description="Add where you study and Campuzon will show you which verification methods your institution actually supports." data-id="element-9843" data-mp="2zz40seof1up6" />
            </Panel> : records.map(record => <RecordCard key={record.id} record={record} data-id="element-9845" data-mp="3eorzd857qf97" />)}

          <Panel title="What is never shared" data-id="element-9846" data-mp="2h94eu0nvgvpz">
            <ul className="space-y-2 text-body-sm text-ink" data-id="element-9847" data-mp="2h8cj8pu9yxzp">
              <li data-id="element-9848" data-mp="2m6xlsvx01trb">
                Your institutional identifier —{' '}
                <span className="cz-num" data-id="element-9849" data-mp="2enwncp122qat">
                  {maskIdentifier(primary?.studentIdentifier ?? null)}
                </span>{' '}
                — is held against your record and never appears on your profile, in search, in
                roommate matching, or to another student.
              </li>
              <li data-id="element-9850" data-mp="1zrfcdyws3dos">Your institutional email address is never shown in full to anyone else.</li>
              <li data-id="element-9851" data-mp="1vpu30njgdhjc">Documents you submit are visible only to the reviewer handling your case.</li>
              <li data-id="element-9852" data-mp="1qyvi1anirv9r">
                Internal review notes about your case are never shown to you or to anyone outside the
                review team.
              </li>
              <li data-id="element-9853" data-mp="1lxeibigrueur">
                A suspension, withdrawal or leave of absence is never shown outside you and your
                institution, whatever your privacy settings say.
              </li>
            </ul>
          </Panel>
        </div>}

      {tab === 'Verify' && <VerifyPanel data-id="element-9854" data-mp="1809hpxztu86w" />}

      {tab === 'What it unlocks' && <div className="space-y-5" data-id="element-9855" data-mp="1wpvrola16y20">
          <Panel title="Your trust record" description="This is what other parts of Campuzon are told about you. It carries signals, never evidence." bodyClassName="p-0" data-id="element-9856" data-mp="1wqxc3gcxswwe">
            <ul className="divide-y divide-line" data-id="element-9857" data-mp="1nvzg20eht40u">
              <TrustRow label="Academic identity verified" value={trust.academicIdentityVerified} data-id="element-9858" data-mp="1ryvvji8qkk0k" />
              <TrustRow label="Confirmed by the institution" value={trust.institutionVerified} data-id="element-9859" data-mp="1dzml6z77ok4i" />
              <TrustRow label="Currently a student" value={trust.currentStudent} data-id="element-9860" data-mp="1iz8zzd2ou8ff" />
              <TrustRow label="Alumni" value={trust.isAlumni} data-id="element-9861" data-mp="14zpuks19hb6t" />
            </ul>
            <div className="border-t border-line px-5 py-3.5 sm:px-6" data-id="element-9862" data-mp="190tafejb7781">
              <p className="text-body-sm text-ink" data-id="element-9863" data-mp="19130110fzcd7">
                Verification level:{' '}
                <span className="font-medium" data-id="element-9864" data-mp="2ze0jf0huw5cl">{LEVEL_LABEL[trust.level]}</span>
                {trust.expiresAt && <span className="text-ink-muted" data-id="element-9865" data-mp="33chs3ogxdnw4"> · valid until {formatDate(trust.expiresAt)}</span>}
              </p>
              <p className="mt-1 text-caption text-ink-subtle" data-id="element-9866" data-mp="nqf62widr4ez">
                Derived from your record every time it is asked for — never stored as a flag. An
                expired verification stops reporting you as a current student on its own.
              </p>
            </div>
          </Panel>

          <Panel title="Features and their requirements" description="Every requirement below is configuration, not code buried in a screen." bodyClassName="p-0" data-id="element-9867" data-mp="1beopjrs4fg2y">
            <ul className="divide-y divide-line" data-id="element-9868" data-mp="1kf3dkywgz9a5">
              {policies.map(policy => {
            const decision = can(policy.id);
            return <li key={policy.id} className="px-5 py-4 sm:px-6" data-id="element-9869" data-mp="3m79cdocasc6x">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-9870" data-mp="3m9lv43pvqql1">
                      <div className="min-w-0" data-id="element-9871" data-mp="3haj0ld7j02rb">
                        <p className="text-body font-semibold text-ink" data-id="element-9872" data-mp="1l6365zysoir8">{policy.feature}</p>
                        <p className="text-body-sm text-ink-muted" data-id="element-9873" data-mp="1q3uyuw79k8wh">
                          {policy.surface} · needs {policy.requirement.toLowerCase()}
                        </p>
                      </div>
                      <Badge tone={decision.allowed ? 'ok' : 'neutral'} data-id="element-9874" data-mp="1z5b46pj81ktu">
                        {decision.allowed ? 'Available to you' : 'Not yet'}
                      </Badge>
                    </div>
                    {!decision.allowed && <p className="mt-2 max-w-[74ch] text-body-sm text-ink" data-id="element-9875" data-mp="2915wydh5swac">
                        {decision.message}
                        {policy.fallback && <span className="text-ink-muted" data-id="element-9876" data-mp="2isimgyfhjad7"> {policy.fallback}</span>}
                      </p>}
                  </li>;
          })}
            </ul>
          </Panel>
        </div>}

      {tab === 'History' && <div className="space-y-5" data-id="element-9877" data-mp="g767kly06ccm">
          <Panel title="Nothing is overwritten" data-id="element-9878" data-mp="g40k1b50vij0">
            <p className="max-w-[78ch] text-body-sm text-ink" data-id="element-9879" data-mp="b68qkcoqbocp">
              Every change to your academic record is appended, with what it was, what it became, who
              changed it, and why. A value the institution established is never silently replaced by
              something you type — the record keeps both and is flagged for review instead.
            </p>
          </Panel>

          {records.map(record => <Panel key={record.id} title={institutionOf(record.institutionId)?.name ?? 'Institution'} bodyClassName="p-0" data-id="element-9880" data-mp="uw2nf56awtns">
              <ol className="divide-y divide-line" data-id="element-9881" data-mp="l4okenf55bpw">
                {record.history.map(entry => <li key={entry.id} className="px-5 py-4 sm:px-6" data-id="element-9882" data-mp="1imevcg1jqqed">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1" data-id="element-9883" data-mp="1dtvglfr4hp7z">
                      <p className="text-body font-medium text-ink" data-id="element-9884" data-mp="m6akq5vn0whe">{entry.field}</p>
                      <p className="text-body-sm text-ink-subtle" data-id="element-9885" data-mp="qx0sfprjdbf9">{formatDate(entry.at)}</p>
                    </div>
                    <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-9886" data-mp="c95xe2q22uz0">
                      {entry.before ? <>
                          <span className="text-ink-muted" data-id="element-9887" data-mp="h5ejy16l1qg7">{entry.before}</span> →{' '}
                          <span className="font-medium" data-id="element-9888" data-mp="14xv0266rf8cv">{entry.after}</span>
                        </> : entry.after}
                    </p>
                    <p className="mt-1 text-caption text-ink-subtle" data-id="element-9889" data-mp="vygad8yf31tf">
                      {entry.changedBy} · {entry.reason}
                    </p>
                  </li>)}
              </ol>
            </Panel>)}

          {myCases.length > 0 && <Panel title="Your verification cases" bodyClassName="p-0" data-id="element-9890" data-mp="3kpr8bp28zy4r">
              <ul className="divide-y divide-line" data-id="element-9891" data-mp="3d3vq7sv47dvw">
                {myCases.map(entry => <li key={entry.id} className="px-5 py-4 sm:px-6" data-id="element-9892" data-mp="386dgw1d6qwnd">
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-id="element-9893" data-mp="3n2upwlxwjz6r">
                      <span className="cz-num text-body font-medium text-ink" data-id="element-9894" data-mp="3i3rvrwjgnddk">{entry.id}</span>
                      <Badge plain data-id="element-9895" data-mp="2ue72ajpokxq1">{entry.state.replace('_', ' ')}</Badge>
                    </div>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-9896" data-mp="2pf485ub8obwu">
                      {METHOD_LABEL[entry.method]} · opened {formatDate(entry.submittedAt)}
                    </p>
                    {entry.decision.reason && <p className="mt-1.5 max-w-[74ch] text-body-sm text-ink" data-id="element-9897" data-mp="2zd0jon8398vh">
                        {entry.decision.reason}
                      </p>}
                  </li>)}
              </ul>
            </Panel>}
        </div>}
    </div>;
}
function TrustRow({
  label,
  value
}: {
  label: string;
  value: boolean;
}) {
  return <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3.5 sm:px-6" data-id="element-9898" data-mp="2nybn88coj8u">
      <span className="text-body text-ink" data-id="element-9899" data-mp="3tu9ftd8nq04e">{label}</span>
      <Badge tone={value ? 'ok' : 'neutral'} icon={value ? <ShieldCheckIcon className="h-4 w-4" data-id="element-9901" data-mp="3ouwitzo2nco3" /> : undefined} data-id="element-9900" data-mp="3twvoxhb74noq">
        {value ? 'Yes' : 'No'}
      </Badge>
    </li>;
}
function RecordCard({
  record
}: {
  record: AcademicRecord;
}) {
  const {
    institutions,
    declare
  } = useVerification();
  const institution = institutions.find(entry => entry.id === record.institutionId);
  const campus = institution?.campuses.find(entry => entry.id === record.campusId);
  const [editing, setEditing] = useState(false);
  const [programme, setProgramme] = useState(record.programme.value);
  const days = daysUntil(record.verification.expiresAt);
  return <Panel title={institution?.name ?? 'Institution'} description={`${campus?.name ?? 'Campus'} · ${record.academicSession}`} action={<AcademicBadge record={record} institutionName={institution?.shortName ?? 'Institution'} owner data-id="element-9903" data-mp="2r1wwmwb3rsuu" />} data-id="element-9902" data-mp="2w8btf4r5pan6">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9904" data-mp="2w1t0mvjsvkae">
        <FieldRow label={institution?.structure.divisionLabel ?? 'Division'} field={record.division} data-id="element-9905" data-mp="310mfoseqjjco" />
        <FieldRow label="Department" field={record.department} data-id="element-9906" data-mp="23ddqonfizs5x" />
        <FieldRow label="Programme" field={record.programme} data-id="element-9907" data-mp="28boyytq69um1" />
        {record.level && institution?.structure.levelLabel && <FieldRow label={institution.structure.levelLabel} field={record.level} data-id="element-9908" data-mp="2daicgm5gllm7" />}
        <div data-id="element-9909" data-mp="2i20gxk6oy4bx">
          <dt className="text-body-sm text-ink-muted" data-id="element-9910" data-mp="2i2jwwvcs6inb">Status</dt>
          <dd className="text-body text-ink" data-id="element-9911" data-mp="1km4h6e5r93ec">{STATUS_LABEL[record.status]}</dd>
          <dd className="mt-0.5 text-caption text-ink-subtle" data-id="element-9912" data-mp="1kk09dl4qjk8s">
            {record.statusSource === 'declared' ? 'You told us this' : 'Established by the institution'}
          </dd>
        </div>
        <div data-id="element-9913" data-mp="1cloltouqwcmk">
          <dt className="text-body-sm text-ink-muted" data-id="element-9914" data-mp="1clybtcfsijs9">
            {institution?.structure.identifierLabel ?? 'Identifier'}
          </dt>
          <dd className="cz-num text-body text-ink" data-id="element-9915" data-mp="1r9rqmux1xi9s">
            {maskIdentifier(record.studentIdentifier)}
          </dd>
          <dd className="mt-0.5 text-caption text-ink-subtle" data-id="element-9916" data-mp="1mdkm96kb0ety">Held, never public</dd>
        </div>
      </dl>

      {record.verification.state === 'requires_reverification' && <div className="mt-4 rounded-md border-2 border-warn-200 bg-warn-50 p-4" data-id="element-9917" data-mp="todowawl5vaa">
          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-9918" data-mp="18kl755ofommx">
            <ShieldAlertIcon aria-hidden className="h-5 w-5 shrink-0 text-warn-700" data-id="element-9919" data-mp="13jxj8svi4lwx" />
            Academic information needs review
          </p>
          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-9920" data-mp="2k0tavaflq406">
            What you entered differs from what your institution holds. This is not an accusation —
            records go out of date and departments change. The institution-sourced value is kept and
            a reviewer will settle it. You can add a correction note at any time.
          </p>
        </div>}

      {days !== null && days <= 30 && record.verification.state === 'verified' && <p className="mt-4 text-body-sm text-ink" data-id="element-9921" data-mp="2hktu6lwd5885">
          {days > 0 ? `Your verification expires in ${days} days. We will remind you before it does — nothing changes on your profile until it actually lapses.` : 'Your verification has lapsed. Your record and history are intact; renew it to restore the badge.'}
        </p>}

      <div className="mt-4 border-t border-line pt-4" data-id="element-9922" data-mp="30inzopya91ls">
        {editing ? <div className="max-w-xl" data-id="element-9923" data-mp="3o8s6t7aojjhj">
            <Field label="Programme" htmlFor={`prog-${record.id}`} hint={record.programme.source === 'declared' ? 'Changing this keeps your verification level, because nobody had confirmed it.' : 'Your institution established this value. Editing it will not overwrite theirs — it raises a review instead.'} data-id="element-9924" data-mp="3o7pdkspvx4yg">
              <Input id={`prog-${record.id}`} value={programme} onChange={event => setProgramme(event.target.value)} data-id="element-9925" data-mp="1vd99nlgndczz" />
            </Field>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-9926" data-mp="2o3rccldglccv">
              <Button variant="primary" size="sm" onClick={() => {
            declare(record.id, 'programme', programme, 'Edited by the account holder');
            setEditing(false);
          }} data-id="element-9927" data-mp="2j3lgsu42j5pi">
                Save
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-id="element-9928" data-mp="36tpigvxp4vdv">
                Cancel
              </Button>
            </div>
          </div> : <Button variant="secondary" size="sm" icon={<HistoryIcon className="h-4 w-4" data-id="element-9930" data-mp="thd1chexjey1" />} onClick={() => setEditing(true)} data-id="element-9929" data-mp="ojto9ktyx87o">
            Correct this record
          </Button>}
      </div>
    </Panel>;
}
function FieldRow({
  label,
  field
}: {
  label: string;
  field: {
    value: string;
    source: string;
    confirmedAt: string | null;
  };
}) {
  return <div data-id="element-9931" data-mp="17h4mr37rzxm2">
      <dt className="text-body-sm text-ink-muted" data-id="element-9932" data-mp="17he2lxz3woe1">{label}</dt>
      <dd className="text-body text-ink" data-id="element-9933" data-mp="1cfxo5x9sfy5v">{field.value}</dd>
      <dd className="mt-0.5 text-caption text-ink-subtle" data-id="element-9934" data-mp="1ceea8dbltz79">
        {field.source === 'declared' ? 'You told us this' : field.source === 'institution' ? `Confirmed by the institution on ${formatDate(field.confirmedAt)}` : field.source === 'integration' ? `From the institutional connection on ${formatDate(field.confirmedAt)}` : `From a reviewed document on ${formatDate(field.confirmedAt)}`}
      </dd>
    </div>;
}
function VerifyPanel() {
  const {
    primary,
    institutions,
    domains,
    verifyEmail,
    openCase
  } = useVerification();
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [documentKind, setDocumentKind] = useState('');
  const [files, setFiles] = useState<string[]>([]);
  const [opened, setOpened] = useState<string | null>(null);
  if (!primary) {
    return <Panel data-id="element-9935" data-mp="9uet3z0q5zqt">
        <EmptyState icon={<BuildingIcon className="h-5 w-5" data-id="element-9937" data-mp="9tc4xyusebel" />} title="Add an academic record first" description="Verification methods depend on which institution you are verifying against." data-id="element-9936" data-mp="9stwm3uuo6qb" />
      </Panel>;
  }
  const institution = institutions.find(entry => entry.id === primary.institutionId);
  const approved = domains.filter(entry => entry.institutionId === primary.institutionId && entry.state === 'approved');
  const held = levelRank(primary.verification.level);
  const ceiling = levelRank(institution?.maxLevelSupported ?? 'email_verified');
  return <div className="space-y-5" data-id="element-9938" data-mp="151nt8s6jloug">
      <Panel title="The levels, and what each one actually proves" data-id="element-9939" data-mp="154tgs2ziwio2">
        <ol className="space-y-3" data-id="element-9940" data-mp="qdjgs5y329k4">
          {LEVEL_ORDER.map((level, index) => {
          const reached = held >= index;
          const possible = index <= ceiling;
          return <li key={level} className={`rounded-md border p-4 ${reached ? 'border-ok-200 bg-ok-50' : 'border-line bg-canvas'}`} data-id="element-9941" data-mp="hcl7p98sfbuo" data-mpi={index}>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-id="element-9942" data-mp="7h1fawtfmvao">
                  <p className="text-body font-semibold text-ink" data-id="element-9943" data-mp="cejtb1mb40pj">
                    Level {index} · {LEVEL_LABEL[level as VerificationLevel]}
                  </p>
                  <Badge tone={reached ? 'ok' : possible ? 'neutral' : 'warn'} data-id="element-9944" data-mp="26sknvaatq1jt">
                    {reached ? 'Reached' : possible ? 'Available' : `Not possible at ${institution?.shortName ?? 'this institution'} yet`}
                  </Badge>
                </div>
                <p className="mt-1.5 max-w-[76ch] text-body-sm text-ink" data-id="element-9945" data-mp="kqtf55ny1ahq">
                  {LEVEL_MEANING[level as VerificationLevel]}
                </p>
              </li>;
        })}
        </ol>
        {ceiling < LEVEL_ORDER.length - 1 && <p className="mt-4 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-9946" data-mp="6slh0uf6834z">
            {institution?.shortName} has no institutional data connection with Campuzon yet, so the
            top level genuinely cannot be reached here. We would rather say that than offer a method
            that does not exist.
          </p>}
      </Panel>

      <Panel title="Institutional email" description="Confirms you can receive mail at an approved institutional address. It does not confirm enrolment." data-id="element-9947" data-mp="1r98d2ihpzzv8">
        <div className="max-w-xl" data-id="element-9948" data-mp="1yqofswfm5eca">
          <Field label="Institutional email address" htmlFor="inst-email" hint={approved.length > 0 ? `Approved for ${institution?.shortName}: ${approved.map(entry => entry.domain).join(', ')}` : 'No approved domain is on file for this institution yet.'} data-id="element-9949" data-mp="23rkf0wp0scbd">
            <Input id="inst-email" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder={`you@${approved[0]?.domain ?? 'institution.edu'}`} data-id="element-9950" data-mp="1ksgdsa3pzmu9" />
          </Field>
          <div className="mt-3" data-id="element-9951" data-mp="317848hhb2k0x">
            <Button variant="primary" icon={<MailIcon className="h-4 w-4" data-id="element-9953" data-mp="366azja7hhbvp" />} disabled={!email.includes('@')} onClick={() => setResult(verifyEmail(primary.id, email))} data-id="element-9952" data-mp="3166h3en0m72u">
              Send verification code
            </Button>
          </div>
          {result && <p className={`mt-3 max-w-[70ch] rounded-md border p-3 text-body-sm ${result.ok ? 'border-ok-200 bg-ok-50 text-ink' : 'border-warn-200 bg-warn-50 text-ink'}`} data-id="element-9954" data-mp="pci4x0jlc5b7">
              {result.message}
            </p>}
          <p className="mt-3 max-w-[74ch] text-caption text-ink-subtle" data-id="element-9955" data-mp="6fh1vl9lkf9w">
            Your address is stored masked and is never shown in full to anyone else. A domain is only
            trusted if it is on the institution's approved register — typing one does not add it.
          </p>
        </div>
      </Panel>

      <Panel title="Academic documents" description="Reviewed by the Campuzon verification team. Accepted documents are set by your institution." data-id="element-9956" data-mp="1lwqw9igm90di">
        <div className="max-w-xl" data-id="element-9957" data-mp="1qx55h3tyegd4">
          <Field label="Document type" htmlFor="doc-kind" data-id="element-9958" data-mp="20zsf2j0qff9g">
            <Select id="doc-kind" value={documentKind} onChange={event => setDocumentKind(event.target.value)} data-id="element-9959" data-mp="2113fq9yvs6wc">
              <option value="" data-id="element-9960" data-mp="1r7wd2wviz4zy">Choose a document</option>
              {(institution?.acceptedDocuments ?? []).map(entry => <option key={entry} value={entry} data-id="element-9961" data-mp="1n61cxvpbyxnr">
                  {entry}
                </option>)}
            </Select>
          </Field>
          <div className="mt-3" data-id="element-9962" data-mp="1d8n0e76j2t0q">
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-line-strong px-4 text-body-sm font-medium text-ink hover:border-brand-300" data-id="element-9963" data-mp="1dbr8vgr10ax7">
              <FileUpIcon aria-hidden className="h-4 w-4" data-id="element-9964" data-mp="14at0yndgvj9c" />
              Choose file
              <input type="file" className="sr-only" accept="image/jpeg,image/png,application/pdf" onChange={event => {
              const file = event.target.files?.[0];
              if (file) setFiles(current => [...current, file.name]);
            }} data-id="element-9965" data-mp="32o2ucfbvs861" />
            </label>
          </div>
          {files.length > 0 && <ul className="mt-3 space-y-1.5" data-id="element-9966" data-mp="mxkvabrkiv09">
              {files.map(name => <li key={name} className="text-body-sm text-ink" data-id="element-9967" data-mp="r1iu0lcze3ss">
                  {name}{' '}
                  <span className="text-ink-subtle" data-id="element-9968" data-mp="1erd8t8gv22fo">
                    — held securely, visible only to the reviewer
                  </span>
                </li>)}
            </ul>}
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-9969" data-mp="15pvv1wqxecu5">
            <Button variant="primary" disabled={documentKind === '' || files.length === 0} onClick={() => setOpened(openCase(primary.id, 'document', files))} data-id="element-9970" data-mp="1aoguvv29mui2">
              Submit for review
            </Button>
            <Button variant="secondary" icon={<BuildingIcon className="h-4 w-4" data-id="element-9972" data-mp="3mwvs74kxgbcj" />} onClick={() => setOpened(openCase(primary.id, 'institution_review', files))} data-id="element-9971" data-mp="3mygnwxiza0bz">
              Ask my institution to confirm instead
            </Button>
          </div>
          {(documentKind === '' || files.length === 0) && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-9973" data-mp="3ry349fu3rwp0">
              Choose a document type and attach a file before submitting.
            </p>}
          {opened && <p className="mt-3 rounded-md border border-ok-200 bg-ok-50 p-3 text-body-sm text-ink" data-id="element-9974" data-mp="2za74u3d5fqoj">
              Case <span className="cz-num font-medium" data-id="element-9975" data-mp="3dzd4fsfwfiwy">{opened}</span> is open. You will be told when
              a decision is recorded, and if it is refused you will be told exactly why and how to
              appeal.
            </p>}
        </div>
      </Panel>

      <Panel title="Verifying later is fine" data-id="element-9976" data-mp="2gj68ln3ey98o">
        <p className="max-w-[78ch] text-body-sm text-ink" data-id="element-9977" data-mp="2ggade6iy9ynx">
          You can keep using Campuzon without verifying. Some things need it — communities restricted
          to current students, roommate matching, selling in the marketplace — and each of those says
          so at the point you reach it, along with what you can do instead. Nothing about your general
          access depends on it.
        </p>
      </Panel>
    </div>;
}